// src/components/DashboardV2.jsx
import React, { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import useSkus from "../hooks/useSkus";
import { Menu as HMenu, Transition } from "@headlessui/react";
import {
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  updateDoc,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
} from "firebase/firestore";

import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";

import FreightCalculator from "./FreightCalculator";
import COA from "../data/coa.json";
import EditBillModal from "./EditBillModal";
import RolesEditor from "./RolesEditor";

/* ---------- Theme ---------- */
const APP_BG = "#EAF0F7";

/* ---------- Static ---------- */
const EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
  "Outside UAE",
];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ---------- Tiny notice ---------- */
function NoticeInline({ type = "ok", children, onClose }) {
  const border =
    type === "err" ? "border-red-300" : type === "warn" ? "border-amber-300" : "border-emerald-300";
  return (
    <div className={`mb-3 rounded-lg border ${border} bg-white px-3 py-2 text-sm flex items-start gap-2`}>
      <span>{type === "err" ? "❌" : type === "warn" ? "⚠️" : "✅"}</span>
      <div className="flex-1">{children}</div>
      {onClose && (
        <button className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Close">
          ×
        </button>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
const getInitials = (value) => {
  if (!value) return "MM";
  const base = value.includes("@") ? value.split("@")[0] : value;
  const parts = base.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  const w = parts[0];
  const a = w?.[0] ?? "M",
    b = w?.[w.length - 1] ?? a;
  return (a + b).toUpperCase();
};

const toNum = (v, fb = 0) => (Number.isFinite(Number(v)) ? Number(v) : fb);

const fxToAED = (currency, fxRateToAED) => {
  const fx = Number(fxRateToAED || 1);
  return currency === "AED" ? 1 : (Number.isFinite(fx) && fx > 0 ? fx : 1);
};

const fmtCur = (currency, amount) => {
  const n = Number(amount) || 0;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency });
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
};

const toDubai = (d) => new Date(d.toLocaleString("en-US", { timeZone: "Asia/Dubai" }));
const startOfDay = (d) => {
  const x = toDubai(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfWeek = (d) => {
  const x = startOfDay(d);
  const day = x.getDay();
  const diff = (day + 6) % 7; // Monday start
  x.setDate(x.getDate() - diff);
  return x;
};
const startOfMonth = (d) => {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
};
const ymdKey = (d) => {
  const z = toDubai(d);
  return `${z.getFullYear()}-${String(z.getMonth() + 1).padStart(2, "0")}-${String(z.getDate()).padStart(2, "0")}`;
};
const dayLabel = (d) =>
  toDubai(d).toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" });

const uniqRoles = (roles) => {
  const seen = new Set();
  return roles.filter((r) => {
    const k = `${r.id}::${r.name || ""}`.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const buildLastNDaysSeries = (rows, { n = 30, dateField = "timestamp", qtyField = "qty" } = {}) => {
  const today = startOfDay(new Date());
  const start = new Date(today);
  start.setDate(start.getDate() - (n - 1));

  const labels = [];
  const keys = [];
  const buckets = {};
  for (let i = 0; i < n; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    labels.push(dayLabel(d));
    const k = ymdKey(d);
    keys.push(k);
    buckets[k] = 0;
  }

  for (const r of rows) {
    const raw = r?.[dateField];
    if (!raw) continue;
    const dt = raw?.toDate ? raw.toDate() : new Date(raw);
    if (!(dt instanceof Date) || isNaN(dt)) continue;
    const k = ymdKey(dt);
    if (k in buckets) buckets[k] += Number(r?.[qtyField] ?? 0);
  }

  return { labels, data: keys.map((k) => buckets[k]) };
};

/* find price/cost for a sale (by SKU, or by Filter Type matching stock name) */
const findPriceCostForSale = (sale, stockData) => {
  if (!sale) return { price: 0, cost: 0 };
  const sku = (sale.sku || "").trim();
  const name = (sale.filterType || sale.name || "").trim().toLowerCase();
  let hit = null;
  if (sku) hit = stockData.find((i) => (i.sku || "") === sku);
  if (!hit && name) hit = stockData.find((i) => (i.name || "").trim().toLowerCase() === name);
  if (hit) return { price: Number(hit.price) || 0, cost: Number(hit.cost) || 0 };
  return { price: Number(sale.price) || 0, cost: Number(sale.cost) || 0 };
};

/* Identify Payable/Purchase rows stored inside "expenses" */
const isPayableRow = (x) => {
  const k = (x?.entryKind || x?.kind || x?.type || "").toLowerCase();
  if (k.includes("payable") || k.includes("purchase") || k.includes("bill")) return true;
  if ((x?.vendor || x?.supplier || x?.oem) && (x?.billNo || x?.invoiceNo || x?.proformaNo)) return true;
  return false;
};

const isOverdue = (due) => {
  if (!due) return false;
  const d = due?.toDate ? due.toDate() : new Date(due);
  if (Number.isNaN(d.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);

  return dd < today;
};

const dueTextClass = (due) =>
  isOverdue(due) ? "text-orange-600 font-semibold" : "text-sky-700";


/* Normalize payable totals */
const normalizeBill = (x) => {
  const totalAED = toNum(x.totalAED ?? x.totalAed ?? x.total ?? x.amountAED ?? x.amount, 0);
  const paidAED = toNum(x.paidAED, 0);
  const outstandingAED = toNum(x.outstandingAED ?? x.outstandingAed, Math.max(totalAED - paidAED, 0));
  const status = outstandingAED === 0 ? "Paid" : paidAED > 0 ? "Partial" : "Open";
  return { ...x, totalAED, paidAED, outstandingAED, status };
};

export default function DashboardV2() {

  // helpers
const toDateInput = (v) => {
  if (!v) return "";
  const d = v?.toDate ? v.toDate() : v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseDateInput = (s) => {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// open modal
const openEditBill = (b) => {
  setEditBill({
    ...b,
    vendor: b.vendor || b.supplier || b.oem || "",
    billNo: b.billNo || b.invoiceNo || b.proformaNo || "",
    billType: b.billType || "Invoice",
    currency: b.currency || "AED",
    fxRateToAED: String(b.fxRateToAED || 1),
    billDate: toDateInput(b.billDate || b.date),
    dueDate: toDateInput(b.dueDate),
    note: b.note || "",
    items: Array.isArray(b.items) && b.items.length ? b.items : [{ item: "", qty: "", value: "" }],
  });
  setShowEditBill(true);
};

// save changes
const saveEditedBill = async () => {
  if (!editBill?.id) return;

  try {
    setSavingEditBill(true);

    const fx = Number(editBill.fxRateToAED || 1) || 1;
    const currency = editBill.currency || "AED";

    const items = (editBill.items || []).map((r) => ({
      item: r.item || "",
      qty: Number(r.qty || 0),
      value: Number(r.value || 0), // in selected currency
    }));

    const totalOriginal = items.reduce((s, r) => s + (Number(r.qty) * Number(r.value)), 0);
    const totalAED = currency === "AED" ? totalOriginal : totalOriginal * fx;

    // IMPORTANT: you’re storing payables in "expenses" (as you did for markBillPaid)
    await updateDoc(doc(db, "expenses", editBill.id), {
      vendor: editBill.vendor,
      billNo: editBill.billNo,
      billType: editBill.billType,
      currency,
      fxRateToAED: fx,
      items,
      note: editBill.note || "",
      billDate: editBill.billDate ? Timestamp.fromDate(parseDateInput(editBill.billDate)) : null,
      dueDate: editBill.dueDate ? Timestamp.fromDate(parseDateInput(editBill.dueDate)) : null,

      // totals
      totalOriginal,
      totalAED,

      updatedAt: serverTimestamp(),
    });

    setShowEditBill(false);
    setEditBill(null);
  } catch (e) {
    console.error("saveEditedBill error:", e);
  } finally {
    setSavingEditBill(false);
  }
};

  /* notices */
  const [stockNotice, setStockNotice] = useState(null);
  const [salesNotice, setSalesNotice] = useState(null);
  const [salesFormNotice, setSalesFormNotice] = useState(null);
  const [stockFormNotice, setStockFormNotice] = useState(null);

  // ===== Edit Payable (Bill) =====
const [showEditBill, setShowEditBill] = useState(false);
const [editBill, setEditBill] = useState(null);
const [savingEditBill, setSavingEditBill] = useState(false);
const [showBills, setShowBills] = useState(false); // NEW: separate bills modal


  /* COA selections for entry forms */
  const [incomeCoa, setIncomeCoa] = useState(null);
  const [expenseCoa, setExpenseCoa] = useState(null);

  /* SKU MASTER */
  const {
    data: skuMaster = [],
    options: skuOptions = [],
    map: skusMap = {},
    loading: skusLoading,
    error: skusError,
  } = useSkus();

  /* UI */
  const [showForm, setShowForm] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [showExp, setShowExp] = useState(false);

  /* STOCK VIEW (Requirement #1) */
  const [stockViewOpen, setStockViewOpen] = useState(false);
  const [stockViewMode, setStockViewMode] = useState(() => localStorage.getItem("az_stockViewMode") || "All"); // All | Low | Active | Hide
  const [stockSearch, setStockSearch] = useState("");
  useEffect(() => localStorage.setItem("az_stockViewMode", stockViewMode), [stockViewMode]);

  /* search */
  const [salesSearch, setSalesSearch] = useState("");

  /* data */
  const [stockData, setStockData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [expensesData, setExpensesData] = useState([]); // local expenses + income + payables/bills

  /* auth / roles */
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [rolePerms, setRolePerms] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showRoles, setShowRoles] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIsAdmin(false);
        setDisplayName("");
        setRolePerms(null);
        setIsAuthorized(false);
        return;
      }

      setUser(firebaseUser);
      let name = firebaseUser.displayName || firebaseUser.email || "";

      try {
        const allowSnap = await getDoc(doc(db, "allowedUsers", firebaseUser.uid));
        const exists = allowSnap.exists();
        setIsAuthorized(exists);

        if (exists) {
          const data = allowSnap.data();
          setIsAdmin((data.role || "").toLowerCase() === "admin");
          name = data.fullName || name;

          const rid = data.roleId || null;
          if (rid) {
            const rSnap = await getDoc(doc(db, "roles", rid));
            setRolePerms(rSnap.exists() ? rSnap.data()?.perms || null : null);
          } else {
            setRolePerms(null);
          }
        } else {
          setIsAdmin(false);
          setRolePerms(null);
        }
      } catch (e) {
        console.error("auth bootstrap:", e);
        setIsAuthorized(false);
        setIsAdmin(false);
        setRolePerms(null);
      }

      setDisplayName(name.trim());
    });

    return () => unsub();
  }, []);

  /* settings */
  const [settings, setSettings] = useState({
    vatPct: 5,
    currencyMode: "AED",
    customSymbol: "د.إ",
    useLandedCost: true,
  });

  const [showPrefs, setShowPrefs] = useState(false);
  const [localPrefs, setLocalPrefs] = useState(settings);

  useEffect(() => setLocalPrefs(settings), [settings]);
  useEffect(() => {
    (async () => {
      try {
        const s = await getDoc(doc(db, "settings", "global"));
        if (s.exists()) setSettings((p) => ({ ...p, ...s.data() }));
      } catch {}
    })();
  }, []);

  const fmtMoney = useCallback(
    (amount) => {
      const n = Number(amount) || 0;
      if (settings.currencyMode === "AED")
        return n.toLocaleString("en-AE", { style: "currency", currency: "AED" });
      const symbol = settings.currencyMode === "AR" ? "د.إ" : settings.customSymbol || "د.إ";
      return `${symbol} ${n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    [settings.currencyMode, settings.customSymbol]
  );

  /* role-gated dashboard bits */
  const showCharts = isAdmin || !!rolePerms?.dashboard?.charts || !!rolePerms?.dashboard?.view;
  const showKPIs = isAdmin || !!rolePerms?.dashboard?.kpis || !!rolePerms?.dashboard?.view;
  const showSalesTable = isAdmin || !!rolePerms?.dashboard?.salesTable || !!rolePerms?.dashboard?.view;

  /* listeners */
  useEffect(() => {
    if (!user || !isAuthorized) return;
    const unsub = onSnapshot(
      query(collection(db, "stock")),
      (snap) => setStockData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("stock listener:", err)
    );
    return () => unsub();
  }, [user, isAuthorized]);

  useEffect(() => {
    if (!user) return;

    const canReadSales = isAdmin || !!rolePerms?.sales?.read || !!rolePerms?.dashboard?.view;
    if (!canReadSales) {
      setSalesData([]);
      return;
    }

    const unsub = onSnapshot(
      query(collection(db, "sales"), orderBy("timestamp", "desc")),
      (snap) => setSalesData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("sales listener:", err)
    );
    return () => unsub();
  }, [user, isAdmin, rolePerms]);

  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(
      query(collection(db, "expenses"), orderBy("date", "asc")),
      (snap) => setExpensesData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("expenses listener:", err)
    );

    return () => unsub();
  }, [user]);

  /* ---------- Entry form (Sale/Stock) ---------- */
  const [entryType, setEntryType] = useState("sale");
  const [customer, setCustomer] = useState("");
  const [outlet, setOutlet] = useState("");
  const [emirates, setEmirates] = useState("");
  const [qty, setQty] = useState("");
  const [salesman, setSalesman] = useState("");
  const [sku, setSku] = useState("");
  const [filterType, setFilterType] = useState("");
  const [vendor, setVendor] = useState("");
  const [threshold, setThreshold] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [unitPrice, setUnitPrice] = useState("");

  // auto-fill filterType from stock or skuMaster when SKU changes (sale)
  useEffect(() => {
    if (entryType !== "sale" || !sku) return;
    const fromStock = stockData.find((i) => i.sku === sku);
    const fromMaster = skusMap[sku];
    const name = fromStock?.name || fromMaster?.name || "";
    setFilterType((prev) => (prev !== name ? name : prev));
  }, [sku, stockData, skusMap, entryType]);

  const incomeOptions = useMemo(() => {
    const arr = Array.isArray(COA) ? COA : [];
    const byType = arr.filter((a) => (a.type || "").toLowerCase() === "income");
    const src = byType.length ? byType : arr;
    return src.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, account: a }));
  }, []);

  const expenseOptions = useMemo(() => {
    const arr = Array.isArray(COA) ? COA : [];
    const byType = arr.filter((a) => {
      const t = (a.type || "").toLowerCase();
      return t === "expense" || t === "cost of goods sold";
    });
    const src = byType.length ? byType : arr;
    return src.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, account: a }));
  }, []);

  const handleAddEntry = async () => {
    try {
      if (entryType === "sale") {
        const canCreate = isAdmin || !!rolePerms?.sales?.create;
        if (!canCreate) {
          setSalesFormNotice({ type: "err", msg: "You don't have permission to create sales." });
          return;
        }
        if (!customer || !outlet || !emirates || !qty || !salesman || !sku) {
          setSalesFormNotice({ type: "err", msg: "Fill Customer, Outlet, Emirates, Qty, Salesman, and SKU." });
          return;
        }
        await addDoc(collection(db, "sales"), {
          customer,
          outlet,
          emirates,
          qty: toNum(qty, 0),
          salesman,
          sku,
          filterType,
          timestamp: serverTimestamp(),
          incomeAccount: incomeCoa?.account?.name || incomeCoa?.label || "",
          glIncome: incomeCoa
            ? {
                code: incomeCoa.account?.code || incomeCoa.value,
                name: incomeCoa.account?.name || incomeCoa.label,
                type: "Income",
              }
            : null,
        });
        setSalesFormNotice({ type: "ok", msg: "Sale entry added." });
      } else {
        const canCreate = isAdmin || !!rolePerms?.stock?.create || !!rolePerms?.stock?.update;
        if (!canCreate) {
          setStockFormNotice({ type: "err", msg: "You don't have permission to create/update stock." });
          return;
        }
        if (!vendor.trim()) {
          setStockFormNotice({ type: "err", msg: "Vendor is required for stock entries." });
          return;
        }
        if (!sku.trim()) {
          setStockFormNotice({ type: "err", msg: "SKU is required." });
          return;
        }

        const existing = stockData.find((i) => i.sku === sku);
        if (existing) {
          await updateDoc(doc(db, "stock", existing.id), {
            name: filterType,
            vendor: vendor.trim(),
            available: toNum(existing.available, 0) + toNum(qty, 0),
            reorder: toNum(threshold, existing.reorder ?? 0),
            price: toNum(unitPrice, existing.price ?? 0),
            cost: toNum(unitCost, existing.cost ?? 0),
            updatedAt: serverTimestamp(),
            expenseAccount: expenseCoa?.account?.name || expenseCoa?.label || existing.expenseAccount || "",
            glExpense: expenseCoa
              ? {
                  code: expenseCoa.account?.code || expenseCoa.value,
                  name: expenseCoa.account?.name || expenseCoa.label,
                  type: expenseCoa.account?.type || expenseCoa.type || "Expense",
                }
              : existing.glExpense || null,
          });
          setStockFormNotice({ type: "ok", msg: "Stock updated." });
        } else {
          await addDoc(collection(db, "stock"), {
            sku,
            name: filterType,
            vendor: vendor.trim(),
            available: toNum(qty, 0),
            reorder: toNum(threshold, 0),
            price: toNum(unitPrice, 0),
            cost: toNum(unitCost, 0),
            updatedAt: serverTimestamp(),
            expenseAccount: expenseCoa?.account?.name || expenseCoa?.label || "",
            glExpense: expenseCoa
              ? {
                  code: expenseCoa.account?.code || expenseCoa.value,
                  name: expenseCoa.account?.name || expenseCoa.label,
                  type: expenseCoa.account?.type || expenseCoa.type || "Expense",
                }
              : null,
          });
          setStockFormNotice({ type: "ok", msg: "Stock item created." });
        }
      }

      // reset form
      setCustomer("");
      setOutlet("");
      setEmirates("");
      setQty("");
      setSalesman("");
      setSku("");
      setFilterType("");
      setThreshold("");
      setUnitCost("");
      setUnitPrice("");
      setVendor("");
      setIncomeCoa(null);
      setExpenseCoa(null);
    } catch (e) {
      console.error(e);
      (entryType === "sale" ? setSalesFormNotice : setStockFormNotice)({
        type: "err",
        msg: "Failed to add entry.",
      });
    }
  };

  /* Export low stock items (full CSV) */
  const handleExportLowStockCsv = useCallback(() => {
    if (!stockData || !stockData.length) {
      alert("No stock data available to export.");
      return;
    }
    const rows = stockData.filter((item) => {
      const avail = toNum(item.available, 0);
      const reord = toNum(item.reorder ?? item.threshold, 0);
      return reord > 0 && avail < reord;
    });

    if (!rows.length) {
      alert("No low-stock items found.");
      return;
    }

    const headers = ["SKU", "Name", "Available", "Reorder Level", "Vendor", "Cost", "Price"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

    const lines = [
      headers.join(","),
      ...rows.map((r) => [r.sku, r.name, r.available, r.reorder ?? r.threshold, r.vendor, r.cost, r.price].map(escape).join(",")),
    ];

    const csv = lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `low_stock_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [stockData]);

  /* ---------- Row edit (stock) ---------- */
  const [stockEditing, setStockEditing] = useState({});
  const [stockDrafts, setStockDrafts] = useState({});

  const canEditStockRow = isAdmin || !!rolePerms?.stock?.update;

  const beginEditStock = (item) => {
    if (!canEditStockRow) {
      setStockNotice({ type: "err", msg: "You don't have permission to edit stock." });
      return;
    }
    setStockEditing((m) => ({ ...m, [item.id]: true }));
    setStockDrafts((m) => ({
      ...m,
      [item.id]: {
        vendor: item.vendor ?? "",
        available: item.available ?? "",
        reorder: item.reorder ?? item.threshold ?? "",
        cost: item.cost ?? "",
        price: item.price ?? "",
      },
    }));
  };
  const changeStockDraft = (id, field, value) => setStockDrafts((m) => ({ ...m, [id]: { ...m[id], [field]: value } }));

  const saveStockRow = async (id) => {
    try {
      if (!canEditStockRow) {
        setStockNotice({ type: "err", msg: "You don't have permission to save stock." });
        return;
      }
      const d = stockDrafts[id];
      if (!d) return;

      const payload = {
        vendor: d.vendor,
        available: toNum(d.available, 0),
        reorder: toNum(d.reorder, 0),
        cost: toNum(d.cost, 0),
        price: toNum(d.price, 0),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, "stock", id), payload);

      setStockNotice({ type: "ok", msg: "Stock saved." });
      setStockEditing((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
      setStockDrafts((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
    } catch (e) {
      console.error(e);
      setStockNotice({ type: "err", msg: "Save failed." });
    }
  };

  /* ---------- Row edit (sales) - ADMIN ONLY (Requirement #6) ---------- */
  const [salesEditing, setSalesEditing] = useState({});
  const [salesDrafts, setSalesDrafts] = useState({});

  const canEditSalesRow = isAdmin; // requirement: editable admin only

  const beginEditSale = (row) => {
    if (!canEditSalesRow) {
      setSalesNotice({ type: "err", msg: "Only admins can edit sales records." });
      return;
    }
    setSalesEditing((m) => ({ ...m, [row.id]: true }));
    setSalesDrafts((m) => ({
      ...m,
      [row.id]: {
        customer: row.customer || "",
        outlet: row.outlet || "",
        emirates: row.emirates || "",
        sku: row.sku || "",
        filterType: row.filterType || "",
        qty: row.qty ?? "",
        salesman: row.salesman || "",
      },
    }));
  };
  const changeSaleDraft = (id, field, value) => setSalesDrafts((m) => ({ ...m, [id]: { ...m[id], [field]: value } }));

  const saveSaleRow = async (id) => {
    try {
      if (!canEditSalesRow) {
        setSalesNotice({ type: "err", msg: "Only admins can save sales records." });
        return;
      }
      const d = salesDrafts[id];
      if (!d) return;

      const payload = {
        customer: d.customer,
        outlet: d.outlet,
        emirates: d.emirates,
        sku: d.sku,
        filterType: d.filterType,
        qty: toNum(d.qty, 0),
        salesman: d.salesman,
      };

      await updateDoc(doc(db, "sales", id), payload);
      setSalesNotice({ type: "ok", msg: "Sale saved." });

      setSalesEditing((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
      setSalesDrafts((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
    } catch (e) {
      console.error(e);
      setSalesNotice({ type: "err", msg: "Save failed." });
    }
  };

  /* ---------- delete (admin only) ---------- */
  const [askDelete, setAskDelete] = useState({ type: null, id: null });

  const confirmDeleteProceed = async () => {
    const { type, id } = askDelete;
    if (!id || !type) return;

    if (!isAdmin) {
      (type === "sale" ? setSalesNotice : setStockNotice)({ type: "err", msg: "Only admins can delete." });
      setAskDelete({ type: null, id: null });
      return;
    }

    try {
      const col = type === "stock" ? "stock" : type === "sale" ? "sales" : "expenses";
      await deleteDoc(doc(db, col, id));
      (type === "sale" ? setSalesNotice : setStockNotice)({ type: "ok", msg: "Deleted." });
    } catch (e) {
      console.error("Delete error:", e);
      const msg = e?.code ? `${e.code}: ${e.message}` : "Delete failed.";
      (type === "sale" ? setSalesNotice : setStockNotice)({ type: "err", msg });
    } finally {
      setAskDelete({ type: null, id: null });
    }
  };
  const confirmDeleteCancel = () => setAskDelete({ type: null, id: null });

  /* ========= REQUIREMENT #1: Stock View (Full Page) ========= */
  const lowStockNow = useMemo(() => {
    return (stockData || []).filter((it) => {
      const avail = toNum(it.available, 0);
      const ro = toNum(it.reorder ?? it.threshold, 0);
      return ro > 0 && avail < ro;
    });
  }, [stockData]);

  const filteredStockForStockView = useMemo(() => {
    if (stockViewMode === "Hide") return [];
    const base = (stockData || []).filter((item) => {
      const avail = toNum(item.available, 0);
      const reorder = toNum(item.reorder ?? item.threshold, 0);
      const hasValidReorder = reorder > 0;

      if (stockViewMode === "Low") return hasValidReorder && avail < reorder;
      if (stockViewMode === "Active") return avail > 0 && (!hasValidReorder || avail >= reorder);
      return true; // All
    });

    const q = stockSearch.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (i) =>
        (i.sku || "").toLowerCase().includes(q) ||
        (i.name || "").toLowerCase().includes(q) ||
        (i.vendor || "").toLowerCase().includes(q)
    );
  }, [stockViewMode, stockData, stockSearch]);

  /* ========= REQUIREMENT #2: KPI + Total custom date search ========= */
  const now = new Date();
  const t0 = startOfDay(now);
  const w0 = startOfWeek(now);
  const m0 = startOfMonth(now);

  const makeBucket = () => ({ revenue: 0, cost: 0, profit: 0, units: 0 });
  const sumInto = (b, s) => {
    const { price, cost } = findPriceCostForSale(s, stockData);
    const q = Number(s.qty) || 0;
    b.revenue += (Number(price) || 0) * q;
    b.cost += (Number(cost) || 0) * q;
    b.units += q;
    b.profit = b.revenue - b.cost;
  };

  const kpiToday = useMemo(() => {
    const b = makeBucket();
    for (const s of salesData || []) {
      const ts = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      if (!ts || isNaN(ts)) continue;
      if (ts >= t0) sumInto(b, s);
    }
    return b;
  }, [salesData, stockData, t0]);

  const kpiWeek = useMemo(() => {
    const b = makeBucket();
    for (const s of salesData || []) {
      const ts = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      if (!ts || isNaN(ts)) continue;
      if (ts >= w0) sumInto(b, s);
    }
    return b;
  }, [salesData, stockData, w0]);

  const kpiMonth = useMemo(() => {
    const b = makeBucket();
    for (const s of salesData || []) {
      const ts = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      if (!ts || isNaN(ts)) continue;
      if (ts >= m0) sumInto(b, s);
    }
    return b;
  }, [salesData, stockData, m0]);

  const kpiTotalAll = useMemo(() => {
    const b = makeBucket();
    for (const s of salesData || []) sumInto(b, s);
    return b;
  }, [salesData, stockData]);

  const [totalFrom, setTotalFrom] = useState("");
  const [totalTo, setTotalTo] = useState("");

  const kpiTotalCustom = useMemo(() => {
    if (!totalFrom || !totalTo) return null;
    const from = startOfDay(new Date(totalFrom));
    const to = startOfDay(new Date(totalTo));
    if (isNaN(from) || isNaN(to)) return null;

    const endExclusive = new Date(to);
    endExclusive.setDate(endExclusive.getDate() + 1);

    const b = makeBucket();
    for (const s of salesData || []) {
      const ts = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      if (!ts || isNaN(ts)) continue;
      if (ts >= from && ts < endExclusive) sumInto(b, s);
    }
    return b;
  }, [salesData, stockData, totalFrom, totalTo]);

  /* ========= REQUIREMENT #3: Totals + Purchases table (Purchases ≠ Payables) ========= */
  const payablesOnly = useMemo(() => (expensesData || []).filter((x) => isPayableRow(x)).map(normalizeBill), [expensesData]);

  const openBills = useMemo(() => payablesOnly.filter((x) => toNum(x.outstandingAED, 0) > 0), [payablesOnly]);
  const purchasesPaid = useMemo(() => payablesOnly.filter((x) => toNum(x.outstandingAED, 0) <= 0), [payablesOnly]);

  const payableTotals = useMemo(() => {
    const outstandingAED = openBills.reduce((s, x) => s + toNum(x.outstandingAED, 0), 0);
    const totalAllBillsAED = payablesOnly.reduce((s, x) => s + toNum(x.totalAED, 0), 0);
    const paidPurchasesAED = purchasesPaid.reduce((s, x) => s + toNum(x.totalAED, 0), 0);
    return {
      outstandingAED,
      totalAllBillsAED,
      paidPurchasesAED,
      countAll: payablesOnly.length,
      countOpen: openBills.length,
      countPaid: purchasesPaid.length,
    };
  }, [payablesOnly, openBills, purchasesPaid]);

  const purchasesByVendor = useMemo(() => {
    // ✅ Purchases table must show only ACTUAL purchases (PAID), NOT payables
    const map = new Map();
    for (const p of purchasesPaid) {
      const vendorName = (p.vendor || p.supplier || p.oem || "Unknown").trim() || "Unknown";
      const totalAED = toNum(p.totalAED, 0);
      const prev = map.get(vendorName) || { vendor: vendorName, totalPurchases: 0 };
      prev.totalPurchases += totalAED;
      map.set(vendorName, prev);
    }
    return Array.from(map.values()).sort((a, b) => b.totalPurchases - a.totalPurchases);
  }, [purchasesPaid]);

  /* Total Stock Value (AED) */
  const stockValue = useMemo(() => {
    const total = (stockData || []).reduce((s, it) => {
      const avail = toNum(it.available, 0);
      const cost = toNum(it.cost, 0);
      return s + avail * cost;
    }, 0);
    return total;
  }, [stockData]);

  /* ========= REQUIREMENT #4: Big Charts (below KPIs) ========= */
  const localExpensesOnly = useMemo(
    () => (expensesData || []).filter((x) => !isPayableRow(x) && (x.entryKind || "").toLowerCase() !== "income"),
    [expensesData]
  );

  // Top Expenses – Donut (LOCAL expenses only, NOT payables)
const topExpensesDonut = useMemo(() => {
  if (!localExpensesOnly.length) return null;

  const byCat = {};
  for (const x of localExpensesOnly) {
    const amt = toNum(x.total ?? x.amount ?? x.amountAED ?? x.totalAED, 0);
    if (!amt) continue;
    const name = x.gl?.name || x.account || x.category || x.expenseAccount || "Other";
    byCat[name] = (byCat[name] || 0) + amt;
  }

  const entries = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;

  const top = entries.slice(0, 6);
  const othersSum = entries.slice(6).reduce((a, [, v]) => a + v, 0);

  const labels = top.map(([name]) => name);
  const data = top.map(([, v]) => v);

  if (othersSum > 0) {
    labels.push("Others");
    data.push(othersSum);
  }

  const donutColors = [
    "#3b82f6", "#f43f5e", "#fb923c", "#22c55e", "#a855f7",
    "#06b6d4", "#eab308", "#ef4444", "#64748b",
  ];

  const colors = labels.map((_, i) => donutColors[i % donutColors.length]);

  return { labels, data, colors };
}, [localExpensesOnly]);

  // 2025 Income vs Purchases (Income from SALES, Purchases from TOTAL payables (bill totalAED))
  const { incomeVsPurchases2025 } = useMemo(() => {
    const year = 2025;
    const incomeByMonth = Array(12).fill(0);
    const purchasesByMonth = Array(12).fill(0);

    // income from sales
    for (const s of salesData || []) {
      const ts = s.timestamp?.toDate ? s.timestamp.toDate() : s.timestamp;
      if (!ts) continue;
      const d = new Date(ts);
      if (d.getFullYear() !== year) continue;
      const m = d.getMonth();
      const { price } = findPriceCostForSale(s, stockData);
      const q = Number(s.qty || 0);
      incomeByMonth[m] += (Number(price) || 0) * q;
    }

    // purchases from payables (use PAID AED per month, includes partial payments)
for (const b of (payablesOnly || [])) {
  const paid = Number(b.paidAED || 0);
  if (!paid) continue;

  const d =
    (b.paidAt?.toDate ? b.paidAt.toDate() : null) ||
    (b.billDate?.toDate ? b.billDate.toDate() : null) ||
    (b.date?.toDate ? b.date.toDate() : null) ||
    null;

  if (!d || d.getFullYear() !== year) continue;

  const m = d.getMonth(); // 0..11
  purchasesByMonth[m] += paid;
}

    return {
      incomeVsPurchases2025: {
        labels: MONTH_LABELS,
        datasets: [
          { label: "Income (Sales)", data: incomeByMonth, borderWidth: 1 },
          { label: "Purchases (Paid)", data: purchasesByMonth, borderWidth: 1 },
        ],
      },
    };
  }, [salesData, stockData, purchasesPaid]);

  /* ========= REQUIREMENT #5: Stock vs Sales charts (below section #4) ========= */
  const soldBySku = useMemo(() => {
    const a = {};
    for (const s of salesData || []) {
      const k = s.sku || "Unknown";
      a[k] = (a[k] || 0) + Number(s.qty || 0);
    }
    return a;
  }, [salesData]);

  const soldVsStockBarData = useMemo(
    () => ({
      labels: Object.keys(soldBySku),
      datasets: [
        { label: "Sold Qty", data: Object.values(soldBySku), borderWidth: 1 },
        {
          label: "Stock on Hand",
          data: Object.keys(soldBySku).map(
            (code) => Number((stockData.find((i) => i.sku === code) || {}).available || 0)
          ),
          borderWidth: 1,
        },
      ],
    }),
    [soldBySku, stockData]
  );

  const soldVsStockBarOptions = useMemo(
    () => ({
      responsive: true,
      plugins: { legend: { display: true } },
      maintainAspectRatio: false,
    }),
    []
  );

  const { labels: last30Labels, data: last30Units } = useMemo(
    () => buildLastNDaysSeries(salesData, { n: 30 }),
    [salesData]
  );

  const last30Data = useMemo(
    () => ({ labels: last30Labels, datasets: [{ label: "Units sold", data: last30Units, borderWidth: 1 }] }),
    [last30Labels, last30Units]
  );

  /* ========= Sales filtering ========= */
  const filteredSales = useMemo(() => {
    const q = salesSearch.trim().toLowerCase();
    if (!q) return salesData;
    return (salesData || []).filter(
      (s) =>
        (s.sku || "").toLowerCase().includes(q) ||
        (s.customer || "").toLowerCase().includes(q) ||
        (s.outlet || "").toLowerCase().includes(q) ||
        (s.emirates || "").toLowerCase().includes(q) ||
        (s.salesman || "").toLowerCase().includes(q) ||
        (s.filterType || "").toLowerCase().includes(q)
    );
  }, [salesData, salesSearch]);

  /* Low stock bell */
  const [bellOpen, setBellOpen] = useState(false);

  const exportLowStockCsvMini = () => {
    const rows = [["SKU", "Name", "Vendor", "Qty Left", "Reorder"]];
    lowStockNow.forEach((i) =>
      rows.push([i.sku, i.name || "", i.vendor || "", i.available ?? 0, i.reorder ?? i.threshold ?? ""])
    );
    if (rows.length === 1) {
      setStockNotice({ type: "warn", msg: "No low stock items." });
      return;
    }
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `low-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => signOut(auth);

  /* Payables actions (optional) */
  const markBillPaid = async (billId, totalAED) => {
    await updateDoc(doc(db, "expenses", billId), {
      paidAED: totalAED,
      outstandingAED: 0,
      status: "Paid",
      paidAt: serverTimestamp(),
    });
  };

  const addBillPayment = async (billId, addAED) => {
    const snap = await getDoc(doc(db, "expenses", billId));
    if (!snap.exists()) return;

    const data = snap.data();
    const total = Number(data.totalAED || 0);
    const paid = Number(data.paidAED || 0);
    const newPaid = Math.min(paid + Number(addAED || 0), total);
    const outstanding = Math.max(total - newPaid, 0);

    await updateDoc(doc(db, "expenses", billId), {
      paidAED: newPaid,
      outstandingAED: outstanding,
      status: outstanding === 0 ? "Paid" : "Partial",
      lastPaymentAt: serverTimestamp(),
    });
  };

  /* Freight Calculator ESC close */
  useEffect(() => {
    if (!showCalc) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowCalc(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCalc]);

  /* ===== Users & Access modal ===== */
  const [showUsers, setShowUsers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);

  const loadUsersAndRoles = async () => {
    setLoadingUsers(true);
    try {
      const uSnap = await getDocs(collection(db, "allowedUsers"));
      let rows = uSnap.docs.map((d) => ({ id: d.id, _dirty: false, ...d.data() }));

      if (rows.length === 0 && user?.uid) {
        try {
          const meDoc = await getDoc(doc(db, "allowedUsers", user.uid));
          if (meDoc.exists()) rows = [{ id: user.uid, _dirty: false, ...meDoc.data() }];
        } catch {}
      }
      setUsersList(rows);

      try {
        const rSnap = await getDocs(collection(db, "roles"));
        const roles = uniqRoles(rSnap.docs.map((d) => ({ id: d.id, name: d.data()?.name || d.id })));
        setAvailableRoles(roles);
      } catch {
        setAvailableRoles([]);
      }
    } catch (e) {
      console.error("loadUsersAndRoles failed:", e);
      setUsersList([]);
      setStockNotice({ type: "err", msg: e?.message || "Failed to load users/roles." });
    } finally {
      setLoadingUsers(false);
    }
  };

  /* Default roles */
  const DEFAULT_PERMS_MANAGER = {
    dashboard: { view: true, charts: true, kpis: true, salesTable: true },
    stock: { read: true, create: true, update: true, delete: false },
    sales: { read: true, create: true, update: true, delete: false },
    reports: { view: true, export: true },
  };
  const DEFAULT_PERMS_SALES = {
    dashboard: { view: true, charts: true, kpis: true, salesTable: true },
    stock: { read: false, create: false, update: false, delete: false },
    sales: { read: true, create: true, update: false, delete: false },
    reports: { view: false, export: false },
  };
  const DEFAULT_PERMS_VIEWER = {
    dashboard: { view: true, charts: true, kpis: true, salesTable: true },
    stock: { read: false, create: false, update: false, delete: false },
    sales: { read: true, create: false, update: false, delete: false },
    reports: { view: false, export: false },
  };

  const ensureDefaultRoles = async () => {
    const defs = [
      { id: "Inventory Manager", data: { name: "Inventory Manager", perms: DEFAULT_PERMS_MANAGER } },
      { id: "Sales User", data: { name: "Sales User", perms: DEFAULT_PERMS_SALES } },
      { id: "Viewer", data: { name: "Viewer", perms: DEFAULT_PERMS_VIEWER } },
    ];
    try {
      await Promise.all(defs.map((r) => setDoc(doc(db, "roles", r.id), r.data, { merge: true })));
    } catch (e) {
      console.error("ensureDefaultRoles failed:", e);
      setStockNotice({ type: "err", msg: "Failed to seed default roles (permissions)." });
    }
  };

  const openUsers = async () => {
    setShowUsers(true);
    await ensureDefaultRoles();
    await loadUsersAndRoles();
  };

  const saveUserRole = async (u) => {
    try {
      await updateDoc(doc(db, "allowedUsers", u.id), { role: u.role || "user", roleId: u.roleId || null });
      setUsersList((arr) => arr.map((x) => (x.id === u.id ? { ...x, _dirty: false } : x)));
      setStockNotice({ type: "ok", msg: "User updated." });
    } catch (e) {
      console.error(e);
      setStockNotice({ type: "err", msg: "Update failed." });
    }
  };

  /* ===== Expense / Income / Payable modal ===== */
  const [savingExp, setSavingExp] = useState(false);
  const [expNotice, setExpNotice] = useState(null);
  const [expMode, setExpMode] = useState("expense");
  
  const [exp, setExp] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    vat: "5",
    ref: "",
    note: "",
    coa: null,
  });

  const [pay, setPay] = useState({
    vendor: "",
    billType: "Proforma",
    billNo: "",
    billDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    currency: "AED",
    fxRateToAED: "1",
    paidAED: "",
    items: [{ item: "", qty: "", value: "" }],
    note: "",
  });

  const resetExpModal = () => {
    setExpNotice(null);
    setSavingExp(false);
    setExpMode("expense");
    setExp({
      date: new Date().toISOString().slice(0, 10),
      amount: "",
      vat: "5",
      ref: "",
      note: "",
      coa: null,
    });
    setPay({
      vendor: "",
      billType: "Proforma",
      billNo: "",
      billDate: new Date().toISOString().slice(0, 10),
      dueDate: "",
      currency: "AED",
      fxRateToAED: "1",
      paidAED: "",
      items: [{ item: "", qty: "", value: "" }],
      note: "",
    });
  };

  const saveOtherExpenseOrIncome = async () => {
    const amt = Number(exp.amount);
    if (!exp.date) return setExpNotice({ type: "err", msg: "Please choose a date." });
    if (!exp.coa) return setExpNotice({ type: "err", msg: `Please choose a ${expMode} account.` });
    if (!Number.isFinite(amt) || amt <= 0) return setExpNotice({ type: "err", msg: "Amount must be a positive number." });

    try {
      setSavingExp(true);
      const vatPct = exp.vat === "5" ? 5 : 0;
      const vatAmount = +((amt * vatPct) / 100).toFixed(2);
      const total = +(amt + vatAmount).toFixed(2);
      const when = Timestamp.fromDate(new Date(exp.date));

      const payload = {
        entryKind: expMode, // expense | income
        date: when,
        dateStr: exp.date,
        amount: amt,
        vatPct,
        vatAmount,
        total,
        ref: exp.ref?.trim() || "",
        note: exp.note?.trim() || "",
        gl: {
          code: exp.coa?.account?.code || exp.coa?.value || "",
          name: exp.coa?.account?.name || exp.coa?.label || "",
          type: expMode === "income" ? "Income" : "Expense",
        },
        createdAt: serverTimestamp(),
        createdBy: { uid: user?.uid || null, name: displayName || "", email: user?.email || "" },

        // legacy aliases
        account: exp.coa?.account?.name || exp.coa?.label || "",
        category: exp.coa?.account?.name || exp.coa?.label || "",
        expenseAccount: exp.coa?.account?.name || exp.coa?.label || "",
        amountAED: amt,
        vat: vatPct,
        reference: exp.ref?.trim() || "",
        notes: exp.note?.trim() || "",
        timestamp: when,
        type: "otherEntry",
      };

      await addDoc(collection(db, "expenses"), payload);

      setExpNotice({ type: "ok", msg: "Saved." });
      setSavingExp(false);
      setExp((p) => ({ ...p, amount: "", ref: "", note: "", coa: null }));
    } catch (e) {
      console.error(e);
      setSavingExp(false);
      setExpNotice({ type: "err", msg: e?.message || "Failed to save." });
    }
  };

  const savePayableBill = async () => {
  if (!pay.vendor.trim()) return setExpNotice({ type: "err", msg: "Vendor/OEM is required." });
  if (!pay.billNo.trim()) return setExpNotice({ type: "err", msg: "Invoice/Proforma number is required." });
  if (!pay.billDate) return setExpNotice({ type: "err", msg: "Bill date is required." });

  const fx = toNum(pay.fxRateToAED, 1);
  if (!Number.isFinite(fx) || fx <= 0) return setExpNotice({ type: "err", msg: "FX rate must be a positive number." });

  const items = (pay.items || []).map((r) => ({
    item: (r.item || "").trim(),
    qty: toNum(r.qty, 0),
    value: toNum(r.value, 0), // unit value in selected currency
  }));

  // ✅ IMPORTANT: subtotal must be qty * value (NOT value only)
const subTotal = items.reduce(
  (s, r) => s + (Number(r.qty) || 0) * (Number(r.value) || 0),
  0
);

if (subTotal <= 0)
  return setExpNotice({ type: "err", msg: "Enter at least 1 item with Qty × Value > 0." });
  const totalOriginal = +subTotal.toFixed(2);
  const totalAED = +(totalOriginal * (pay.currency === "AED" ? 1 : fx)).toFixed(2);

  const paidAED = toNum(pay.paidAED, 0);
  const outstandingAED = Math.max(+(totalAED - paidAED).toFixed(2), 0);

  try {
    setSavingExp(true);

    const billDateTs = Timestamp.fromDate(new Date(pay.billDate));
    const dueDateTs = pay.dueDate ? Timestamp.fromDate(new Date(pay.dueDate)) : null;

    const status = outstandingAED === 0 ? "Paid" : paidAED > 0 ? "Partial" : "Open";

    const payload = {
      entryKind: "payable",
      date: billDateTs,
      dateStr: pay.billDate,

      vendor: pay.vendor.trim(),
      billType: pay.billType,
      billNo: pay.billNo.trim(),
      billDate: billDateTs,
      dueDate: dueDateTs,

      currency: pay.currency,
      fxRateToAED: pay.currency === "AED" ? 1 : fx,

      items,

      // ✅ keep both fields consistent
      subTotal: totalOriginal,
      totalOriginal,
      totalAED,

      paidAED,
      outstandingAED,
      status,

      note: pay.note?.trim() || "",
      createdAt: serverTimestamp(),
      createdBy: { uid: user?.uid || null, name: displayName || "", email: user?.email || "" },

      // aliases
      supplier: pay.vendor.trim(),
      oem: pay.vendor.trim(),
      invoiceNo: pay.billNo.trim(),
      proformaNo: pay.billNo.trim(),
      type: "bill",
    };

    await addDoc(collection(db, "expenses"), payload);

    setExpNotice({ type: "ok", msg: "Payable saved." });
    setSavingExp(false);

    setPay((p) => ({
      ...p,
      vendor: "",
      billNo: "",
      dueDate: "",
      currency: "AED",
      fxRateToAED: "1",
      paidAED: "",
      items: [{ item: "", qty: "", value: "" }],
      note: "",
    }));
  } catch (e) {
    console.error(e);
    setSavingExp(false);
    setExpNotice({ type: "err", msg: e?.message || "Failed to save payable." });
  }
};

  /* ===== MAIN RENDER ===== */
  return (
    <div className="min-h-screen" style={{ background: APP_BG }}>
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 bg-[#0b4a73] text-white">
        <div className="w-full h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/azlogo512.png" alt="All Zones" className="w-12 h-12 rounded" />
            <span className="text-xl sm:text-2xl font-bold tracking-wide" style={{ fontFamily: "Cinzel" }}>
              ALL ZONES WATER TREATMENT
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowForm((s) => !s)}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold"
            >
              {showForm ? "Hide Entry Form" : "Add New Entry"}
            </button>

            <button
              onClick={() => {
                setExpMode("expense");
                setShowExp(true);
                setExpNotice(null);
              }}
              className="px-3 py-1.5 rounded-lg bg-fuchsia-600 text-white text-xs font-semibold"
            >
              New Expense / Income
            </button>

            <button
  onClick={() => {
    setShowBills(true);
    setExpNotice(null);
  }}
  className="px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold"
>
  New Bills
</button>

 

            <Link
              to="/reports"
              className="px-3 py-1.5 rounded-lg bg-slate-800/70 hover:bg-slate-800 text-white text-xs font-semibold"
              title="Reports"
            >
              Reports
            </Link>

            <button
              onClick={() => setShowCalc(true)}
              className="px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-semibold"
            >
              Freight Calculator
            </button>

            {/* Low stock bell */}
            <div className="relative ml-1">
              <button
                type="button"
                onClick={() => setBellOpen((b) => !b)}
                className="relative h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                title="Low stock alerts"
              >
                <BellIcon className="h-5 w-5 text-white" />
                {lowStockNow.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] leading-none px-1.5 py-0.5 rounded-full">
                    {lowStockNow.length}
                  </span>
                )}
              </button>

              {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white text-slate-800 rounded-xl shadow-lg border p-3 z-50">
                  <div className="font-semibold mb-2">Low Stock Alerts</div>
                  {lowStockNow.length === 0 ? (
                    <div className="text-sm text-gray-500">No low stock items.</div>
                  ) : (
                    <ul className="max-h-64 overflow-auto text-sm divide-y">
                      {lowStockNow.slice(0, 20).map((i) => (
                        <li key={i.id} className="py-2">
                          <div className="font-medium">{i.sku}</div>
                          <div className="text-xs text-gray-600">
                            {i.name || "—"} • Left: <b>{i.available ?? 0}</b> • Reorder:{" "}
                            <b>{i.reorder ?? i.threshold ?? "-"}</b>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 flex justify-between gap-2">
                    <button
                      onClick={() => {
                        exportLowStockCsvMini();
                        setBellOpen(false);
                      }}
                      className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm"
                    >
                      Export (mini)
                    </button>
                    <button
                      onClick={() => {
                        handleExportLowStockCsv();
                        setBellOpen(false);
                      }}
                      className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm"
                    >
                      Export full CSV
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User menu */}
            <HMenu as="div" className="relative ml-1">
              <HMenu.Button className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-sm font-bold">{getInitials(displayName || user?.email)}</span>
                </div>
                <span className="hidden sm:block text-sm truncate max-w-[160px]">{displayName || "User"}</span>
              </HMenu.Button>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <HMenu.Items className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none text-slate-700">
                  <div className="p-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <UserCircleIcon className="h-6 w-6 text-slate-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{displayName || "User"}</div>
                        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                      </div>
                    </div>
                  </div>

                  <div className="py-1">
                    {isAdmin && (
                      <HMenu.Item>
                        {({ active }) => (
                          <button
                            onClick={openUsers}
                            className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm ${
                              active ? "bg-slate-100" : ""
                            }`}
                          >
                            <UserCircleIcon className="h-5 w-5" />
                            Users &amp; Access
                          </button>
                        )}
                      </HMenu.Item>
                    )}

                    {isAdmin && (
  <HMenu.Item>
    {({ active }) => (
      <button
        onClick={() => setShowRoles(true)}
        className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm ${
          active ? "bg-slate-100" : ""
        }`}
      >
        <UserCircleIcon className="h-5 w-5" />
        Role Manager
      </button>
    )}
  </HMenu.Item>
)}

                    <HMenu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => setShowPrefs(true)}
                          className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm ${
                            active ? "bg-slate-100" : ""
                          }`}
                        >
                          <Cog6ToothIcon className="h-5 w-5" />
                          Preferences
                        </button>
                      )}
                    </HMenu.Item>

                    <HMenu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm ${
                            active ? "bg-red-50 text-red-600" : "text-red-600"
                          }`}
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5" />
                          Logout
                        </button>
                      )}
                    </HMenu.Item>
                  </div>
                </HMenu.Items>
              </Transition>
            </HMenu>
          </div>
        </div>
      </header>

      {/* ========= Requirement #1: STOCK VIEW (Full Page View) ========= */}
      {stockViewOpen && (
        <div className="fixed inset-0 z-[9998] bg-white">
          <div className="h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between border-b bg-white">
            <div className="flex items-center gap-3">
              <div className="font-semibold text-slate-800">Stock View</div>
              <div className="flex rounded-lg bg-slate-100 overflow-hidden text-xs">
                {[
                  ["All", "All"],
                  ["Low", "Low Stock"],
                  ["Active", "Active"],
                  ["Hide", "Hide"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setStockViewMode(val)}
                    className={`px-3 py-1.5 border-r last:border-r-0 ${
                      stockViewMode === val ? "bg-[#0b4a73] text-white" : "bg-transparent text-slate-700 hover:bg-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                placeholder="Search stock (SKU, name, vendor)"
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm w-64 bg-white"
              />
              <button
                onClick={() => setStockViewOpen(false)}
                className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-500">
                Showing: <b>{stockViewMode}</b> • Rows: <b>{filteredStockForStockView.length}</b>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportLowStockCsvMini}
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold"
                >
                  Export Low Stock (mini)
                </button>
                <button
                  onClick={handleExportLowStockCsv}
                  className="px-3 py-1.5 rounded bg-sky-600 text-white text-xs font-semibold"
                >
                  Export Low Stock (full)
                </button>
              </div>
            </div>

            {stockViewMode === "Hide" ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                Stock View is hidden.
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-auto max-h-[calc(100vh-220px)]">
                  <table className="min-w-full text-[11px]">
                    <thead className="bg-slate-100">
                      <tr className="text-left text-slate-600">
                        <th className="px-2 py-2">SKU</th>
                        <th className="px-2 py-2">Name</th>
                        <th className="px-2 py-2">Vendor</th>
                        <th className="px-2 py-2 text-right">Avail</th>
                        <th className="px-2 py-2 text-right">Re-order</th>
                        <th className="px-2 py-2 text-right">Cost</th>
                        <th className="px-2 py-2 text-right">Price</th>
                        <th className="px-2 py-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStockForStockView.map((item) => {
                        const reorder = toNum(item.reorder ?? item.threshold, 0);
                        const avail = toNum(item.available, 0);
                        const isLow = reorder > 0 && avail < reorder;

                        const editing = !!stockEditing[item.id];
                        const draft = stockDrafts[item.id] || {};

                        return (
                          <tr key={item.id} className={`border-b last:border-b-0 ${isLow ? "bg-red-50" : "bg-white"}`}>
                            <td className="px-2 py-1.5 whitespace-nowrap">{item.sku}</td>
                            <td className="px-2 py-1.5 whitespace-nowrap">{item.name}</td>

                            <td className="px-2 py-1.5 whitespace-nowrap">
                              {editing ? (
                                <input
                                  className="border border-slate-200 rounded px-1 py-0.5 w-full"
                                  value={draft.vendor ?? ""}
                                  onChange={(e) => changeStockDraft(item.id, "vendor", e.target.value)}
                                />
                              ) : (
                                item.vendor || "—"
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-right">
                              {editing ? (
                                <input
                                  type="number"
                                  className="border border-slate-200 rounded px-1 py-0.5 w-16 text-right"
                                  value={draft.available ?? ""}
                                  onChange={(e) => changeStockDraft(item.id, "available", e.target.value)}
                                />
                              ) : (
                                avail
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-right">
                              {editing ? (
                                <input
                                  type="number"
                                  className="border border-slate-200 rounded px-1 py-0.5 w-16 text-right"
                                  value={draft.reorder ?? ""}
                                  onChange={(e) => changeStockDraft(item.id, "reorder", e.target.value)}
                                />
                              ) : (
                                reorder || "-"
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                              {editing ? (
                                <input
                                  type="number"
                                  className="border border-slate-200 rounded px-1 py-0.5 w-20 text-right"
                                  value={draft.cost ?? ""}
                                  onChange={(e) => changeStockDraft(item.id, "cost", e.target.value)}
                                />
                              ) : (
                                fmtMoney(item.cost || 0)
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                              {editing ? (
                                <input
                                  type="number"
                                  className="border border-slate-200 rounded px-1 py-0.5 w-20 text-right"
                                  value={draft.price ?? ""}
                                  onChange={(e) => changeStockDraft(item.id, "price", e.target.value)}
                                />
                              ) : (
                                fmtMoney(item.price || 0)
                              )}
                            </td>

                            <td className="px-2 py-1.5 text-center">
                              {editing ? (
                                <div className="flex justify-center gap-1">
                                  <button
                                    className="p-1 rounded-full bg-emerald-100 text-emerald-700"
                                    onClick={() => saveStockRow(item.id)}
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    className="p-1 rounded-full bg-slate-100 text-slate-600"
                                    onClick={() => {
                                      setStockEditing((m) => {
                                        const n = { ...m };
                                        delete n[item.id];
                                        return n;
                                      });
                                      setStockDrafts((m) => {
                                        const n = { ...m };
                                        delete n[item.id];
                                        return n;
                                      });
                                    }}
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex justify-center gap-1">
                                  <button
                                    className="p-1 rounded-full bg-slate-100 text-slate-700 disabled:opacity-50"
                                    onClick={() => beginEditStock(item)}
                                    disabled={!canEditStockRow}
                                    title={canEditStockRow ? "Edit" : "No permission"}
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  {isAdmin && (
                                    <button
                                      className="p-1 rounded-full bg-red-100 text-red-700"
                                      onClick={() => setAskDelete({ type: "stock", id: item.id })}
                                      title="Delete"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {filteredStockForStockView.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-2 py-4 text-center text-slate-500">
                            No stock items to display.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Freight calc modal */}
      {showCalc && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-3" onClick={() => setShowCalc(false)}>
          <div
            className="bg-white w-full max-w-5xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-slate-800">Freight Calculator</div>
              <button
                type="button"
                onClick={() => setShowCalc(false)}
                className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="max-h-[80vh] overflow-auto p-4">
              <FreightCalculator onClose={() => setShowCalc(false)} />
            </div>
          </div>
        </div>
      )}

      {/* ===== Body ===== */}
      <div className="w-full px-3 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 pb-6">
        <div className="py-4 flex flex-col gap-4">
          {/* Title row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-800">Inventory &amp; Sales Dashboard</h1>
              <p className="text-xs text-slate-500">Real-time stock, sales, purchases/payables, and expenses overview.</p>
            </div>

            {/* Requirement #1 entry point */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setStockViewOpen(true)}
                className="px-4 py-2 rounded-xl bg-[#0b4a73] text-white text-sm font-semibold shadow-sm"
              >
                Stock View
              </button>

              <input
                type="text"
                value={salesSearch}
                onChange={(e) => setSalesSearch(e.target.value)}
                placeholder="Search sales (customer, emirates, sku, salesman)"
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm w-72 bg-white"
              />
            </div>
          </div>

          {/* Notices */}
          <div className="mt-1 space-y-1">
            {stockNotice && (
              <NoticeInline type={stockNotice.type} onClose={() => setStockNotice(null)}>
                {stockNotice.msg}
              </NoticeInline>
            )}
            {salesNotice && (
              <NoticeInline type={salesNotice.type} onClose={() => setSalesNotice(null)}>
                {salesNotice.msg}
              </NoticeInline>
            )}
          </div>

          {/* Add Entry form */}
          {showForm && (
            <div className="mt-1 rounded-2xl bg-white shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-800">
                  {entryType === "sale" ? "Add New Sale Entry" : "Add New Stock Entry"}
                </h2>
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={() => setEntryType("sale")}
                    className={`px-2 py-1 rounded-full border ${
                      entryType === "sale"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    Sale
                  </button>
                  <button
                    onClick={() => setEntryType("stock")}
                    className={`px-2 py-1 rounded-full border ${
                      entryType === "stock"
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    Stock
                  </button>
                </div>
              </div>

              {entryType === "sale" ? (
                <>
                  {salesFormNotice && (
                    <NoticeInline type={salesFormNotice.type} onClose={() => setSalesFormNotice(null)}>
                      {salesFormNotice.msg}
                    </NoticeInline>
                  )}
                  <div className="grid md:grid-cols-3 gap-3 text-xs">
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Customer"
                      value={customer}
                      onChange={(e) => setCustomer(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Outlet"
                      value={outlet}
                      onChange={(e) => setOutlet(e.target.value)}
                    />
                    <select
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      value={emirates}
                      onChange={(e) => setEmirates(e.target.value)}
                    >
                      <option value="">Emirates</option>
                      {EMIRATES.map((em) => (
                        <option key={em} value={em}>
                          {em}
                        </option>
                      ))}
                    </select>

                    <Select
                      options={skuOptions}
                      isClearable
                      placeholder="SKU"
                      className="text-xs"
                      onChange={(opt) => setSku(opt?.value || "")}
                      value={sku ? skuOptions.find((o) => o.value === sku) || null : null}
                    />

                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Filter Type / Name"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    />

                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Qty"
                      type="number"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                    />

                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Salesman"
                      value={salesman}
                      onChange={(e) => setSalesman(e.target.value)}
                    />

                    <Select
                      className="text-xs"
                      placeholder="Income Account (COA)"
                      options={incomeOptions}
                      value={incomeCoa}
                      onChange={setIncomeCoa}
                      isClearable
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={handleAddEntry}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold"
                    >
                      Save Sale Entry
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {stockFormNotice && (
                    <NoticeInline type={stockFormNotice.type} onClose={() => setStockFormNotice(null)}>
                      {stockFormNotice.msg}
                    </NoticeInline>
                  )}
                  <div className="grid md:grid-cols-3 gap-3 text-xs">
                    <Select
                      options={skuOptions}
                      isClearable
                      placeholder="SKU"
                      className="text-xs"
                      onChange={(opt) => setSku(opt?.value || "")}
                      value={sku ? skuOptions.find((o) => o.value === sku) || null : null}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Filter Type / Name"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Vendor"
                      value={vendor}
                      onChange={(e) => setVendor(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Qty (Stock In)"
                      type="number"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Re-order Level"
                      type="number"
                      value={threshold}
                      onChange={(e) => setThreshold(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Unit Cost"
                      type="number"
                      value={unitCost}
                      onChange={(e) => setUnitCost(e.target.value)}
                    />
                    <input
                      className="border border-slate-200 rounded-lg px-2 py-1.5"
                      placeholder="Unit Price"
                      type="number"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                    />
                    <Select
                      className="text-xs"
                      placeholder="Expense Account (COA)"
                      options={expenseOptions}
                      value={expenseCoa}
                      onChange={setExpenseCoa}
                      isClearable
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={handleAddEntry}
                      className="px-3 py-1.5 rounded-lg bg-sky-600 text-white text-xs font-semibold"
                    >
                      Save Stock Entry
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ========= Requirement #2: KPI Section ========= */}
          {showKPIs && (
            <div className="mt-2 grid md:grid-cols-4 gap-3">
              {[
                ["Today Sales", kpiToday],
                ["This Week Sales", kpiWeek],
                ["This Month Sales", kpiMonth],
              ].map(([label, bucket]) => (
                <div key={label} className="bg-white rounded-2xl shadow-sm border border-slate-200 px-3 py-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-700">{label}</span>
                    <span className="text-[10px] text-slate-400 uppercase">Sales</span>
                  </div>
                  <div className="text-base font-semibold text-slate-900">{fmtMoney(bucket.revenue)}</div>
                  <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                    <span>Units: {bucket.units}</span>
                    <span>Profit: {fmtMoney(bucket.profit)}</span>
                  </div>
                </div>
              ))}

              {/* Total Sales (with custom date search) */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-3 py-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-700">Total Sales</span>
                  <span className="text-[10px] text-slate-400 uppercase">Custom</span>
                </div>

                <div className="text-base font-semibold text-slate-900">
                  {fmtMoney((kpiTotalCustom || kpiTotalAll).revenue)}
                </div>

                <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                  <span>Units: {(kpiTotalCustom || kpiTotalAll).units}</span>
                  <span>Profit: {fmtMoney((kpiTotalCustom || kpiTotalAll).profit)}</span>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-slate-500 mb-1">From</div>
                    <input
                      type="date"
                      value={totalFrom}
                      onChange={(e) => setTotalFrom(e.target.value)}
                      className="w-full border border-slate-200 rounded px-2 py-1 text-[11px]"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 mb-1">To</div>
                    <input
                      type="date"
                      value={totalTo}
                      onChange={(e) => setTotalTo(e.target.value)}
                      className="w-full border border-slate-200 rounded px-2 py-1 text-[11px]"
                    />
                  </div>
                </div>

                <div className="mt-2 flex justify-end">
                  <button
                    className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-[11px]"
                    onClick={() => {
                      setTotalFrom("");
                      setTotalTo("");
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ========= Requirement #3: Totals Section ========= */}
          <div className="mt-2 grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Total Stock Value</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{fmtMoney(stockValue)}</div>
              <div className="text-xs text-slate-500 mt-2">Based on Available × Cost</div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Total Payables</div>
              <div className="text-2xl font-bold text-rose-700 mt-1">{fmtMoney(payableTotals.outstandingAED)}</div>
              <div className="text-xs text-slate-500 mt-2">
                Open Bills: <b>{payableTotals.countOpen}</b> • Paid Purchases: <b>{payableTotals.countPaid}</b>
              </div>
            </div>
          </div>

          {/* Purchases table (Paid only) */}
          <div className="mt-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-3 overflow-hidden">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-800">
                Purchases (Supplier/OEM) – Total Purchase Value (AED)
              </h3>
              <span className="text-[10px] text-slate-500">{purchasesByVendor.length} suppliers</span>
            </div>

            <div className="overflow-auto">
              <table className="min-w-full text-[11px]">
                <thead className="bg-slate-100">
                  <tr className="text-left text-slate-600">
                    <th className="px-2 py-1.5">Supplier / OEM</th>
                    <th className="px-2 py-1.5 text-right">Total Purchases (Paid)</th>
                  </tr>
                </thead>
                <tbody>
                  {purchasesByVendor.map((r) => (
                    <tr key={r.vendor} className="border-b last:border-b-0">
                      <td className="px-2 py-1.5">{r.vendor}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{fmtMoney(r.totalPurchases)}</td>
                    </tr>
                  ))}
                  {purchasesByVendor.length === 0 && (
                    <tr>
                      <td colSpan={2} className="px-2 py-3 text-center text-slate-500">
                        No paid purchases found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* optional: Open bills (payables) — kept separate so Purchases stays clean */}
            <div className="mt-3 border-t pt-3">
              <div className="text-xs font-semibold text-slate-700 mb-2">Open Bills (Payables)</div>
              <div className="overflow-auto">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-slate-100">
                    <tr className="text-left text-slate-600">
                      <th className="px-2 py-1.5">Supplier</th>
                      <th className="px-2 py-1.5">Invoice/Proforma</th>
                      <th className="px-2 py-1.5">Bill Date</th>
                      <th className="px-2 py-1.5">Currency</th>
                      <th className="px-2 py-1.5 text-right">Total</th>
                      <th className="px-2 py-1.5 text-right">Paid</th>
                      <th className="px-2 py-1.5 text-right">Outstanding</th>
                      <th className="px-2 py-1.5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openBills.map((b) => {
  const billDate =
    b.billDate?.toDate ? b.billDate.toDate()
    : b.date?.toDate ? b.date.toDate()
    : null;

  const dueDate = b.dueDate?.toDate ? b.dueDate.toDate() : null;

  const currency = (b.currency || "AED").toUpperCase();

  const totalOriginal = Number(b.totalOriginal ?? b.subTotal ?? 0) || 0;
  const totalAED = Number(b.totalAED ?? 0) || 0;
  const paidAED = Number(b.paidAED ?? 0) || 0;
  const outstandingAED =
    Number(b.outstandingAED ?? Math.max(totalAED - paidAED, 0)) || 0;

  return (
    <tr key={b.id} className="border-b last:border-b-0">
      <td className="px-2 py-1.5">{b.vendor || b.supplier || b.oem || "—"}</td>

      <td className="px-2 py-1.5">{b.billNo || b.invoiceNo || b.proformaNo || "—"}</td>

      {/* Bill Date + Due Date (small) */}
<td className="px-2 py-1.5">
  <div>{billDate ? billDate.toLocaleDateString("en-GB") : "—"}</div>

  {dueDate ? (
    <div
      className={`text-[10px] ${
        isOverdue(dueDate) ? "text-orange-600 font-semibold" : "text-sky-700"
      }`}
    >
      Due: {dueDate.toLocaleDateString("en-GB")}
    </div>
  ) : (
    <div className="text-[10px] text-slate-400">Due: —</div>
  )}
</td>

      {/* Currency column (ONLY currency text) */}
      <td className="px-2 py-1.5">{currency}</td>

      {/* Total column: original currency + AED below */}
      <td className="px-2 py-1.5 text-right">
        <div className="font-medium">{fmtCur(currency, totalOriginal)}</div>
        {currency !== "AED" && (
          <div className="text-[10px] text-slate-500">{fmtCur("AED", totalAED)}</div>
        )}
      </td>

      <td className="px-2 py-1.5 text-right">{fmtCur("AED", paidAED)}</td>
      <td className="px-2 py-1.5 text-right">{fmtCur("AED", outstandingAED)}</td>

      <td className="px-2 py-1.5 text-center">
        <div className="flex justify-center gap-1">
          <button
            className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px]"
            onClick={() => markBillPaid(b.id, totalAED)}
          >
            Mark Paid
          </button>
          
          <button
            className="px-2 py-0.5 rounded bg-sky-600 text-white text-[10px]"
            onClick={() => {
              const v = prompt("Add payment amount (AED):");
              if (!v) return;
              addBillPayment(b.id, Number(v));
            }}
          >
            Add Payment
          </button>

          {/* ✅ Edit button (hook it up in Fix 2 below) */}
          <button
            className="px-2 py-0.5 rounded bg-slate-800 text-white text-[10px]"
            onClick={() => openEditBill(b)}
          >
            Edit
          </button>

          {isAdmin && (
            <button
              className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px]"
              onClick={() => setAskDelete({ type: "expenses", id: b.id })}
            >
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
})}

                    {openBills.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-2 py-2 text-center text-slate-500">
                          No open bills 🎉
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ========= Requirement #4: Big Charts Section (below KPIs) ========= */}
          {(showCharts || showKPIs) && (
            <div className="mt-3 grid xl:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-800">2025 Income vs Purchases</h3>
                  <span className="text-[11px] text-slate-500">Sales income vs paid purchases</span>
                </div>
                {/* Totals under chart */}
{(() => {
  const incomeArr = incomeVsPurchases2025?.datasets?.[0]?.data || [];
  const purchArr  = incomeVsPurchases2025?.datasets?.[1]?.data || [];

  const totalIncome = incomeArr.reduce((a, b) => a + (Number(b) || 0), 0);
  const totalPurch  = purchArr.reduce((a, b) => a + (Number(b) || 0), 0);
  const net = totalIncome - totalPurch;

  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
        <div className="text-[11px] text-slate-600">Total Income</div>
        <div className="text-sm font-semibold text-slate-900">{fmtCur("AED", totalIncome)}</div>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
        <div className="text-[11px] text-slate-600">Total Purchases</div>
        <div className="text-sm font-semibold text-slate-900">{fmtCur("AED", totalPurch)}</div>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
        <div className="text-[11px] text-slate-600">Net (Income - Purchases)</div>
        <div className={`text-sm font-semibold ${net < 0 ? "text-red-600" : "text-emerald-700"}`}>
          {fmtCur("AED", net)}
        </div>
      </div>
    </div>
  );
})()}

                <div className="h-[360px]">
                  <Bar
                    data={incomeVsPurchases2025}
                    options={{ responsive: true, plugins: { legend: { display: true } }, maintainAspectRatio: false }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-slate-800">Top Expenses</h3>
                  <span className="text-[11px] text-slate-500">Local expenses only</span>
                </div>
                <div className="h-[360px]">
  {topExpensesDonut ? (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-full items-center">
      {/* Donut (left) */}
      <div className="lg:col-span-7 h-full flex items-center justify-center">
        <div className="w-full max-w-[320px] h-[320px]">
          <Doughnut
            data={{
              labels: topExpensesDonut.labels,
              datasets: [
                {
                  data: topExpensesDonut.data,
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              cutout: "65%",
              plugins: {
                legend: { display: false }, // ✅ hide bottom legend
              },
            }}
          />
        </div>
      </div>

      {/* Labels (right) */}
      <div className="lg:col-span-5">
        <div className="space-y-2">
          {(topExpensesDonut.labels || []).map((label, idx) => {
            const val = Number(topExpensesDonut.data?.[idx] || 0);

            return (
              <div key={label} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-3 w-3 rounded-sm"
                    style={{
                      background:
                        topExpensesDonut.colors?.[idx] ||
                        "rgba(0,0,0,0.2)",
                    }}
                  />
                  <span className="text-sm text-slate-800 truncate">
                    {label}
                  </span>
                </div>

                <div className="text-sm font-semibold text-slate-900 whitespace-nowrap">
                  {fmtCur("AED", val)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  ) : (
    <div className="h-full flex items-center justify-center text-slate-500 text-sm">
      No local expenses to chart.
    </div>
  )}
</div>

              </div>
            </div>
          )}

          {/* ========= Requirement #5: Stock vs Sales Charts (below section #4) ========= */}
          {showCharts && (
            <div className="mt-3 grid xl:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Total Sold vs Stock by SKU</h3>
                <div className="h-[360px]">
                  <Bar data={soldVsStockBarData} options={soldVsStockBarOptions} />
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Last 30 Days – Units Sold</h3>
                <div className="h-[360px]">
                  <Bar
                    data={last30Data}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      maintainAspectRatio: false,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ========= Requirement #6: Sales Records Table (clean + admin editable) ========= */}
          {showSalesTable && (
            <div className="mt-3 bg-white rounded-2xl shadow-sm border border-slate-200 p-3 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Sales Records</h3>
                <span className="text-[11px] text-slate-500">{filteredSales.length} entries</span>
              </div>

              <div className="overflow-auto max-h-[520px]">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-slate-100">
                    <tr className="text-left text-slate-600">
                      <th className="px-2 py-2">Date</th>
                      <th className="px-2 py-2">Emirate</th>
                      <th className="px-2 py-2">SKU</th>
                      <th className="px-2 py-2 text-right">Qty</th>
                      <th className="px-2 py-2">Salesman</th>
                      <th className="px-2 py-2">Customer</th>
                      <th className="px-2 py-2">Outlet</th>
                      <th className="px-2 py-2">Filter Type</th>
                      <th className="px-2 py-2 text-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredSales.map((row) => {
                      const editing = !!salesEditing[row.id];
                      const draft = salesDrafts[row.id] || {};

                      const ts = row.timestamp?.toDate
                        ? row.timestamp.toDate()
                        : row.timestamp
                        ? new Date(row.timestamp)
                        : null;

                      const dateStr = ts && !isNaN(ts) ? toDubai(ts).toLocaleDateString("en-GB") : "-";

                      return (
                        <tr key={row.id} className="border-b last:border-b-0 bg-white">
                          <td className="px-2 py-1.5 whitespace-nowrap">{dateStr}</td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-28"
                                value={draft.emirates ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "emirates", e.target.value)}
                              />
                            ) : (
                              row.emirates || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-28"
                                value={draft.sku ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "sku", e.target.value)}
                              />
                            ) : (
                              row.sku || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 text-right">
                            {editing ? (
                              <input
                                type="number"
                                className="border border-slate-200 rounded px-1 py-0.5 w-16 text-right"
                                value={draft.qty ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "qty", e.target.value)}
                              />
                            ) : (
                              row.qty ?? 0
                            )}
                          </td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-28"
                                value={draft.salesman ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "salesman", e.target.value)}
                              />
                            ) : (
                              row.salesman || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-40"
                                value={draft.customer ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "customer", e.target.value)}
                              />
                            ) : (
                              row.customer || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-36"
                                value={draft.outlet ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "outlet", e.target.value)}
                              />
                            ) : (
                              row.outlet || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {editing ? (
                              <input
                                className="border border-slate-200 rounded px-1 py-0.5 w-44"
                                value={draft.filterType ?? ""}
                                onChange={(e) => changeSaleDraft(row.id, "filterType", e.target.value)}
                              />
                            ) : (
                              row.filterType || "-"
                            )}
                          </td>

                          <td className="px-2 py-1.5 text-center">
                            {editing ? (
                              <div className="flex justify-center gap-1">
                                <button
                                  className="p-1 rounded-full bg-emerald-100 text-emerald-700"
                                  onClick={() => saveSaleRow(row.id)}
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  className="p-1 rounded-full bg-slate-100 text-slate-600"
                                  onClick={() => {
                                    setSalesEditing((m) => {
                                      const n = { ...m };
                                      delete n[row.id];
                                      return n;
                                    });
                                    setSalesDrafts((m) => {
                                      const n = { ...m };
                                      delete n[row.id];
                                      return n;
                                    });
                                  }}
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-center gap-1">
                                <button
                                  className="p-1 rounded-full bg-slate-100 text-slate-700 disabled:opacity-50"
                                  onClick={() => beginEditSale(row)}
                                  disabled={!canEditSalesRow}
                                  title={canEditSalesRow ? "Edit" : "Admin only"}
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                                {isAdmin && (
                                  <button
                                    className="p-1 rounded-full bg-red-100 text-red-700"
                                    onClick={() => setAskDelete({ type: "sale", id: row.id })}
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {filteredSales.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-2 py-4 text-center text-slate-500">
                          No sales records to display.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Preferences modal ===== */}
      {showPrefs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 text-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800">Preferences</h3>
              <button onClick={() => setShowPrefs(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-600 mb-1">Currency Mode</label>
                <select
                  className="border border-slate-200 rounded px-2 py-1 w-full"
                  value={localPrefs.currencyMode}
                  onChange={(e) => setLocalPrefs((p) => ({ ...p, currencyMode: e.target.value }))}
                >
                  <option value="AED">AED (system)</option>
                  <option value="AR">Arabic د.إ</option>
                  <option value="CUSTOM">Custom symbol</option>
                </select>
              </div>
              {localPrefs.currencyMode === "CUSTOM" && (
                <div>
                  <label className="block text-slate-600 mb-1">Custom symbol</label>
                  <input
                    className="border border-slate-200 rounded px-2 py-1 w-full"
                    value={localPrefs.customSymbol}
                    onChange={(e) => setLocalPrefs((p) => ({ ...p, customSymbol: e.target.value }))}
                  />
                </div>
              )}
              <div>
                <label className="block text-slate-600 mb-1">VAT percentage</label>
                <input
                  type="number"
                  className="border border-slate-200 rounded px-2 py-1 w-24"
                  value={localPrefs.vatPct}
                  onChange={(e) => setLocalPrefs((p) => ({ ...p, vatPct: Number(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2 text-xs">
              <button
                className="px-3 py-1.5 rounded border border-slate-200 text-slate-600"
                onClick={() => setShowPrefs(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 rounded bg-emerald-600 text-white"
                onClick={async () => {
                  setSettings(localPrefs);
                  try {
                    await setDoc(doc(db, "settings", "global"), localPrefs, { merge: true });
                  } catch {}
                  setShowPrefs(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Users & Access modal ===== */}
      {showUsers && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl p-4 text-xs">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800">Users &amp; Access</h3>
              <button onClick={() => setShowUsers(false)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            {loadingUsers ? (
              <div className="py-4 text-center text-slate-500">Loading users...</div>
            ) : (
              <div className="max-h-[380px] overflow-auto">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-slate-100">
                    <tr className="text-left text-slate-600">
                      <th className="px-2 py-1.5">Name</th>
                      <th className="px-2 py-1.5">Email</th>
                      <th className="px-2 py-1.5">Role</th>
                      <th className="px-2 py-1.5">Role ID</th>
                      <th className="px-2 py-1.5 text-center">Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map((u) => (
                      <tr key={u.id} className="border-b last:border-b-0">
                        <td className="px-2 py-1.5 whitespace-nowrap">{u.fullName || u.displayName || "—"}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{u.email || "—"}</td>
                        <td className="px-2 py-1.5">
                          <select
                            className="border border-slate-200 rounded px-1 py-0.5 w-full"
                            value={u.role || ""}
                            onChange={(e) =>
                              setUsersList((arr) =>
                                arr.map((x) => (x.id === u.id ? { ...x, role: e.target.value, _dirty: true } : x))
                              )
                            }
                          >
                            <option value="">(none)</option>
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="sales">Sales</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            className="border border-slate-200 rounded px-1 py-0.5 w-full"
                            value={u.roleId || ""}
                            onChange={(e) =>
                              setUsersList((arr) =>
                                arr.map((x) => (x.id === u.id ? { ...x, roleId: e.target.value || null, _dirty: true } : x))
                              )
                            }
                          >
                            <option value="">(none)</option>
                            {availableRoles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {u._dirty ? (
                            <button className="px-2 py-0.5 rounded bg-emerald-600 text-white" onClick={() => saveUserRole(u)}>
                              Save
                            </button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {usersList.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-2 py-2 text-center text-slate-500">
                          No users found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== Expense / Income modal (NO PAYABLE) ===== */}
{showExp && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl p-4 text-xs max-h-[85vh] overflow-auto">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-800">
          {expMode === "expense" ? "New Local Expense" : "New Other Income"}
        </h3>
        <button
          onClick={() => {
            setShowExp(false);
            resetExpModal();
          }}
          className="text-slate-400 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      {expNotice && (
        <NoticeInline type={expNotice.type} onClose={() => setExpNotice(null)}>
          {expNotice.msg}
        </NoticeInline>
      )}

      {/* Mode buttons (ONLY 2) */}
      <div className="flex gap-2 mb-3">
        <button
          className={`flex-1 px-2 py-1 rounded-full border ${
            expMode === "expense"
              ? "bg-rose-600 text-white border-rose-600"
              : "bg-white text-slate-700 border-slate-200"
          }`}
          onClick={() => {
            setExpMode("expense");
            setExpNotice(null);
          }}
        >
          Local Expense
        </button>

        <button
          className={`flex-1 px-2 py-1 rounded-full border ${
            expMode === "income"
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white text-slate-700 border-slate-200"
          }`}
          onClick={() => {
            setExpMode("income");
            setExpNotice(null);
          }}
        >
          Other Income
        </button>
      </div>

      {/* Expense / Income form */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="block text-slate-600 mb-1">Date</label>
          <input
            type="date"
            className="border border-slate-200 rounded px-2 py-1 w-full"
            value={exp.date}
            onChange={(e) => setExp((p) => ({ ...p, date: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-slate-600 mb-1">Account (COA)</label>
          <Select
            options={expMode === "income" ? incomeOptions : expenseOptions}
            isClearable
            className="text-xs"
            placeholder={expMode === "income" ? "Select income account" : "Select expense account"}
            value={exp.coa}
            onChange={(opt) => setExp((p) => ({ ...p, coa: opt }))}
          />
        </div>

        <div>
          <label className="block text-slate-600 mb-1">Amount</label>
          <input
            type="number"
            className="border border-slate-200 rounded px-2 py-1 w-full"
            value={exp.amount}
            onChange={(e) => setExp((p) => ({ ...p, amount: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-slate-600 mb-1">VAT</label>
          <select
            className="border border-slate-200 rounded px-2 py-1 w-full"
            value={exp.vat}
            onChange={(e) => setExp((p) => ({ ...p, vat: e.target.value }))}
          >
            <option value="5">5% (standard)</option>
            <option value="0">0%</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-slate-600 mb-1">Reference</label>
          <input
            className="border border-slate-200 rounded px-2 py-1 w-full"
            value={exp.ref}
            onChange={(e) => setExp((p) => ({ ...p, ref: e.target.value }))}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-slate-600 mb-1">Note</label>
          <textarea
            className="border border-slate-200 rounded px-2 py-1 w-full min-h-[70px]"
            value={exp.note}
            onChange={(e) => setExp((p) => ({ ...p, note: e.target.value }))}
          />
        </div>

        <div className="md:col-span-2 flex justify-end gap-2 mt-2">
          <button
            className="px-3 py-1.5 rounded border border-slate-200 text-slate-600"
            onClick={() => {
              setShowExp(false);
              resetExpModal();
            }}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 rounded bg-emerald-600 text-white disabled:opacity-60"
            disabled={savingExp}
            onClick={saveOtherExpenseOrIncome}
          >
            {savingExp ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* ===== New Bills (Payable / Purchase) modal ===== */}
{showBills && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 text-xs">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-800">New Payable / Purchase (Bill/Proforma)</h3>
        <button
          onClick={() => {
            setShowBills(false);
            resetExpModal(); // if this also resets pay state; if not, we’ll make resetPayableModal()
          }}
          className="text-slate-400 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      {expNotice && (
        <NoticeInline type={expNotice.type} onClose={() => setExpNotice(null)}>
          {expNotice.msg}
        </NoticeInline>
      )}

      <div className="space-y-3">
        {/* Payable header fields */}
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="block text-slate-600 mb-1">Vendor / OEM</label>
            <input
              className="border border-slate-200 rounded px-2 py-1 w-full"
              value={pay.vendor}
              onChange={(e) => setPay((p) => ({ ...p, vendor: e.target.value }))}
              placeholder="Supplier / OEM name"
            />
          </div>

          <div>
            <label className="block text-slate-600 mb-1">Type</label>
            <select
              className="border border-slate-200 rounded px-2 py-1 w-full"
              value={pay.billType}
              onChange={(e) => setPay((p) => ({ ...p, billType: e.target.value }))}
            >
              <option value="Proforma">Proforma</option>
              <option value="Invoice">Invoice</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-600 mb-1">Invoice / Proforma No</label>
            <input
              className="border border-slate-200 rounded px-2 py-1 w-full"
              value={pay.billNo}
              onChange={(e) => setPay((p) => ({ ...p, billNo: e.target.value }))}
              placeholder="e.g., PI-123 / INV-456"
            />
          </div>

          <div>
            <label className="block text-slate-600 mb-1">Bill Date</label>
            <input
              type="date"
              className="border border-slate-200 rounded px-2 py-1 w-full"
              value={pay.billDate}
              onChange={(e) => setPay((p) => ({ ...p, billDate: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-slate-600 mb-1">Due Date (optional)</label>
            <input
              type="date"
              className="border border-slate-200 rounded px-2 py-1 w-full"
              value={pay.dueDate}
              onChange={(e) => setPay((p) => ({ ...p, dueDate: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-slate-600 mb-1">Currency</label>
              <select
                className="border border-slate-200 rounded px-2 py-1 w-full"
                value={pay.currency}
                onChange={(e) =>
                  setPay((p) => ({
                    ...p,
                    currency: e.target.value,
                    fxRateToAED: e.target.value === "AED" ? "1" : p.fxRateToAED,
                  }))
                }
              >
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="SAR">SAR</option>
                <option value="OMR">OMR</option>
                <option value="INR">INR</option>
                <option value="CNY">CNY</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-600 mb-1">FX rate → AED</label>
              <input
                type="number"
                step="0.0001"
                className="border border-slate-200 rounded px-2 py-1 w-full"
                value={pay.fxRateToAED}
                onChange={(e) => setPay((p) => ({ ...p, fxRateToAED: e.target.value }))}
                disabled={pay.currency === "AED"}
              />
            </div>
          </div>
        </div>

        {/* Items table */}
        <div className="border rounded-xl overflow-hidden">
          <div className="bg-slate-100 px-3 py-2 text-[11px] font-semibold text-slate-700">
            Item Table (enter Value in selected currency)
          </div>

          <div className="p-3 space-y-2">
            {pay.items.map((r, idx) => (
              <div key={idx} className="grid md:grid-cols-12 gap-2 items-center">
                <input
                  className="border border-slate-200 rounded px-2 py-1 md:col-span-6"
                  placeholder="Item name / details"
                  value={r.item}
                  onChange={(e) =>
                    setPay((p) => {
                      const items = [...p.items];
                      items[idx] = { ...items[idx], item: e.target.value };
                      return { ...p, items };
                    })
                  }
                />

                <input
                  type="number"
                  className="border border-slate-200 rounded px-2 py-1 md:col-span-2"
                  placeholder="Qty"
                  value={r.qty}
                  onChange={(e) =>
                    setPay((p) => {
                      const items = [...p.items];
                      items[idx] = { ...items[idx], qty: e.target.value };
                      return { ...p, items };
                    })
                  }
                />

                <div className="md:col-span-3">
                  <input
                    type="number"
                    className="border border-slate-200 rounded px-2 py-1 w-full"
                    placeholder={`Value (${pay.currency})`}
                    value={r.value}
                    onChange={(e) =>
                      setPay((p) => {
                        const items = [...p.items];
                        items[idx] = { ...items[idx], value: e.target.value };
                        return { ...p, items };
                      })
                    }
                  />
                  <div className="mt-1 text-[10px] text-slate-500">
                    AED:{" "}
                    <b>
                      {(
                        (Number(r.qty || 0) || 0) *
                        (Number(r.value || 0) || 0) *
                        fxToAED(pay.currency, pay.fxRateToAED)
                      ).toFixed(2)}
                    </b>
                  </div>
                </div>

                <button
                  className="md:col-span-1 text-red-600 hover:text-red-700"
                  title="Remove row"
                  onClick={() =>
                    setPay((p) => {
                      const items = p.items.filter((_, i) => i !== idx);
                      return { ...p, items: items.length ? items : [{ item: "", qty: "", value: "" }] };
                    })
                  }
                >
                  ×
                </button>
              </div>
            ))}

            <button
              className="px-3 py-1.5 rounded bg-slate-800 text-white text-xs"
              onClick={() => setPay((p) => ({ ...p, items: [...p.items, { item: "", qty: "", value: "" }] }))}
            >
              + Add New Row
            </button>

            {/* Note + Paid */}
            <div className="grid md:grid-cols-3 gap-3 pt-2">
              <div className="md:col-span-2">
                <label className="block text-slate-600 mb-1">Note</label>
                <input
                  className="border border-slate-200 rounded px-2 py-1 w-full"
                  value={pay.note}
                  onChange={(e) => setPay((p) => ({ ...p, note: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>

              <div>
                <label className="block text-slate-600 mb-1">Paid (AED) optional</label>
                <input
                  type="number"
                  className="border border-slate-200 rounded px-2 py-1 w-full"
                  value={pay.paidAED}
                  onChange={(e) => setPay((p) => ({ ...p, paidAED: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Subtotal boxes (Currency + AED) */}
            {(() => {
              const fx = fxToAED(pay.currency, pay.fxRateToAED);
              const subOriginal = (pay.items || []).reduce(
                (s, r) => s + (Number(r.qty || 0) || 0) * (Number(r.value || 0) || 0),
                0
              );
              const subAED = subOriginal * fx;

              return (
                <div className="mt-2 grid md:grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                    <div className="text-slate-500">Subtotal ({pay.currency})</div>
                    <div className="font-semibold">{fmtCur(pay.currency, subOriginal)}</div>
                  </div>

                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                    <div className="text-slate-500">Subtotal (AED)</div>
                    <div className="font-semibold">{fmtCur("AED", subAED)}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-2 mt-2">
          <button
            className="px-3 py-1.5 rounded border border-slate-200 text-slate-600"
            onClick={() => {
              setShowBills(false);
              resetExpModal();
            }}
          >
            Cancel
          </button>
          <button
            className="px-3 py-1.5 rounded bg-sky-600 text-white disabled:opacity-60"
            disabled={savingExp}
            onClick={savePayableBill}
          >
            {savingExp ? "Saving..." : "Save Payable"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* ===== Edit Payable / Purchase modal ===== */}
<EditBillModal
  show={showEditBill}
  editBill={editBill}
  setEditBill={setEditBill}
  saving={savingEditBill}
  onClose={() => {
    setShowEditBill(false);
    setEditBill(null);
  }}
  onSave={saveEditedBill}
  toDateInput={toDateInput}
  parseDateInput={parseDateInput}
/>

{showRoles && (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">Role Manager</h3>
        <button
          onClick={() => setShowRoles(false)}
          className="text-slate-400 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      <div className="max-h-[75vh] overflow-auto">
        <RolesEditor />
      </div>
    </div>
  </div>
)}

      {/* ===== Delete confirm ===== */}
      {askDelete.id && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 text-xs">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-4">
            <h3 className="font-semibold text-slate-800 mb-2">Confirm delete</h3>
            <p className="text-slate-600 mb-3">
              Are you sure you want to delete this <b>{askDelete.type}</b> record? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1.5 rounded border border-slate-200 text-slate-600" onClick={confirmDeleteCancel}>
                Cancel
              </button>
              <button className="px-3 py-1.5 rounded bg-red-600 text-white" onClick={confirmDeleteProceed}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
